import { userContentDetailsService } from '@/data/services/content/user-content-details-service'
import { userEnrollmentContentInfoService } from '@/data/services/content/user-enrollment-content-info-service'
import { Account } from '@/domain/usecases'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import {
  makeSkillWebContentPageRedirectUrl,
  makeSkillWebHomeRedirectUrl
} from '@/main/factories/http'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { QueryClient } from 'react-query'
import { LoaderFunctionArgs, redirect } from 'react-router-dom'
import RegisteredPlanBaseLoader from '../../registered-plan-base-loader'
import { contentDetailsPromise, enrollmentContentInfoPromise } from './content-loader-promises'

export default class ContentLoader extends RegisteredPlanBaseLoader {
  private static readonly ID_PATH_PARAM_NAME = 'id'
  private contentId: number | undefined
  constructor(
    readonly account: Account.Model,
    readonly queryClient: QueryClient,
    readonly ldClient: LDClient | undefined
  ) {
    super(account, queryClient, ldClient)
  }

  async loader({ params }: LoaderFunctionArgs) {
    const id = parseInt(params[ContentLoader.ID_PATH_PARAM_NAME] ?? '')
    if (isNaN(id) || id <= 0) return null

    this.contentId = id
    const isProductFieldMigrationEnabled = FeatureFlag.Key.PRODUCT_FIELD_MIGRATION.getValue(
      this.ldClient
    )

    const requestContentDetailsPromise = isProductFieldMigrationEnabled
      ? userContentDetailsService.fetchQueryUserContentDetails(id)
      : contentDetailsPromise(this.queryClient, id)

    const requestEnrollmentContentInfoPromise = isProductFieldMigrationEnabled
      ? userEnrollmentContentInfoService.fetchQueryUserEnrollmentContentInfo(id)
      : enrollmentContentInfoPromise(this.queryClient, id)

    const [contentDetails, enrollmentContentInfo] = await Promise.all([
      requestContentDetailsPromise,
      requestEnrollmentContentInfoPromise
    ])

    return this.defer({
      data: {
        userContentDetails: contentDetails,
        userEnrollmentContentInfo: enrollmentContentInfo
      }
    })
  }

  protected fallbackRedirect(): Error | Response {
    return redirect(
      this.contentId
        ? makeSkillWebContentPageRedirectUrl(this.contentId)
        : makeSkillWebHomeRedirectUrl()
    )
  }
}
